import { JSONContent } from '@tiptap/core';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { startWith, take } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { Role } from '../../entities/role/role';
import { Tenant } from '../../entities/tenant/tenant';
import { serverErrorMessages } from '../../services/apollo-error';
import { ApolloService } from '../../services/apollo.service';
import { RolesService } from '../../services/roles/roles.service';
import { ToastService } from '../../services/toast/toast.service';
import * as fromCatalog from '../notification-settings-list/graphql';
import { TENANTS_QUERY, TenantsQueryResponse } from '../tenant-list/graphql';
import {
  ANNOUNCEMENT_DASHBOARD_TTL_DAYS,
  ANNOUNCEMENT_LOCALES,
  ANNOUNCEMENT_REQUIRED_LOCALE,
  announcementTypeOptions,
  emailDefaultHintKey
} from '../announcement-list/announcement-labels';
import {
  ANNOUNCEMENT_BODY_MAX_CHARS,
  isEmptyRichText,
  richTextLength
} from '../announcement-list/announcement-rich-text';
import {
  buildTranslationSegments,
  contentEntryFromSegments,
  TRANSLATE_TEXTS_MUTATION,
  TranslateTextsResponse,
  TranslationFailure,
  translationFailureKey
} from '../announcement-list/announcement-translation';
import * as fromGraphQl from '../announcement-list/graphql';

/** Zurueck zur Liste — nach Erfolg und ueber den Verwerfen-Knopf. */
export const ANNOUNCEMENT_LIST_PATH = '/settings/announcements';

/**
 * Der Server liefert keine „alle Mandanten"-Query. getTenantsPaged ist die einzige Quelle und ist
 * seitenweise; fuer eine Auswahlliste wird deshalb eine bewusst grosse Seite geholt.
 */
const TENANT_PAGE_SIZE = 500;

/** Client-Dok §8.7: absolute http/https-URL, hoechstens 2048 Zeichen. */
const URI_LINK_MAX_CHARS = 2048;

/**
 * Welche Absicht der Screen bedient. Steht als statisches `data.mode` an den drei Routen —
 * /new, /:id/edit und /:id/correct — und entscheidet Beschriftungen, gesperrte Felder und
 * die abgesetzte Mutation.
 */
export type AnnouncementFormMode = 'CREATE' | 'EDIT' | 'CORRECT';

export type TargetingMode = 'ALL' | 'SELECTION';
export type ScheduleMode = 'LEAD' | 'AT';

/**
 * Verfassen, Aendern und Korrigieren einer Ankuendigung (#738, Modi im #743-Nachzug).
 *
 * Eigener Screen statt Overlay: sieben Sprach-Tabs mit je Titel und Text, dazu Zielgruppe,
 * Terminierung und Test-Versand sprengen einen Dialog. Es ist die erste `new`-Route des Repos —
 * bewusster, begruendeter Bruch mit der Konvention (Entscheidung 1 der Spec).
 *
 * EINE Komponente fuer drei Routen (mode()): Template, Validatoren, Sprach-Tabs und Targeting sind
 * in allen drei Faellen dieselben; unterschiedlich sind Beschriftungen, gesperrte Felder und die
 * abgesetzte Mutation. EDIT und CORRECT leben vom Router-State der Liste — das Schema hat keine
 * Einzelabfrage, ein Reload leitet deshalb dorthin zurueck (siehe ngOnInit).
 *
 * `content` und `targeting` sind pass-through-Map-Scalars: das Schema sagt ueber ihre Struktur
 * nichts, und es gibt keinen Codegen, der sie abgleicht. Alle Regeln, die der Server kennt, stehen
 * hier als echte Validatoren — sonst prasseln englische, technische ValidationProblems.
 *
 * Formularzustand wird ueber ein Signal gespiegelt, nicht direkt im Template gelesen: die
 * Wertaenderung entsteht in den ControlValueAccessor-Kindern, und die markiert diese OnPush-Komponente
 * nicht zwangslaeufig als dirty.
 */
@Component({
  selector: 'app-announcement-form',
  templateUrl: './announcement-form.component.html',
  styleUrls: ['./announcement-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AnnouncementFormComponent implements OnInit {

  private readonly apolloService = inject(ApolloService);
  private readonly rolesService = inject(RolesService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly formBuilder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  readonly locales = ANNOUNCEMENT_LOCALES;
  readonly requiredLocale = ANNOUNCEMENT_REQUIRED_LOCALE;

  readonly typeOptions = signal<fromCatalog.NotificationTypeInfo[]>([]);
  readonly tenants = signal<Tenant[]>([]);
  readonly tenantsFailed = signal(false);
  readonly roles = signal<Role[]>([]);

  readonly submitting = signal(false);
  readonly testing = signal(false);

  /**
   * Fachliche Meldungen des Servers, wortwoertlich. Client-Dok §8.1: „Diese Meldung gehoert
   * dem Autor gezeigt, nicht nur geloggt." Bewusst NICHT geparst, um den betroffenen Tab zu
   * markieren — das Praefix ist deutscher Servertext und kann sich aendern; ein stiller
   * Fehlschlag beim Parsen waere schlechter als kein Marker.
   */
  readonly serverErrors = signal<string[]>([]);

  readonly form: FormGroup;

  /** Vorschausprache des Test-Versands. Vorgabe ist die eigene Sprache. */
  readonly previewLocale = signal<string>(ANNOUNCEMENT_REQUIRED_LOCALE);

  /**
   * Idempotenzschluessel dieser Absendeabsicht (Client-Dok §8.3). Ohne ihn lehnt der Server JEDES
   * Anlegen ab — `clientRequestId must not be null`, AnnouncementAdminServiceImpl:137.
   *
   * Feldinitialisierung, kein Signal und kein Nachladen: die Lebensdauer dieser
   * Komponenteninstanz IST die Absendeabsicht. Die Maske ist offen, also gilt derselbe
   * Schluessel; ein neues Oeffnen erzeugt einen neuen. Jeder Mechanismus darueber hinaus waere
   * eine zweite Definition davon.
   *
   * Bewusst NICHT in buildInput(): sendTestAnnouncement teilt denselben Input-Typ, persistiert
   * aber nichts und braucht ihn nicht. Ein mitgesendeter Schluessel waere dort folgenlos, aber
   * irrefuehrend — der Test ist keine Absendeabsicht.
   *
   * uuidv4 statt crypto.randomUUID: Letzteres gibt es unter jsdom nicht, und diese Zeile laeuft
   * im Konstruktor — jeder Test dieser Komponente waere daran gescheitert. Das Paket ist
   * ohnehin Dependency und wird fuer genau diesen Zweck schon in file-gallery und file-list
   * benutzt.
   */
  readonly clientRequestId = uuidv4();

  readonly targetingModeOptions = [
    { label: 'core.announcement.form.targetingAll', value: 'ALL' },
    { label: 'core.announcement.form.targetingSelection', value: 'SELECTION' }
  ];

  readonly scheduleModeOptions = [
    { label: 'core.announcement.form.scheduleLead', value: 'LEAD' },
    { label: 'core.announcement.form.scheduleAt', value: 'AT' }
  ];

  readonly localeOptions = ANNOUNCEMENT_LOCALES.map((locale) => ({
    label: `core.announcement.form.locale.${locale}`,
    value: locale
  }));

  // --- Gespiegelter Formularzustand. Einzige Quelle bleibt das FormGroup.
  private readonly formErrors = signal<ValidationErrors | null>(null);
  private readonly formValid = signal(false);
  private readonly filledLocales = signal<string[]>([]);
  readonly targetingMode = signal<TargetingMode>('ALL');
  readonly scheduleMode = signal<ScheduleMode>('LEAD');
  readonly dashboardBannerEnabled = signal(false);
  readonly selectedTypeKey = signal<string | null>(null);

  /**
   * E-Mail-Vorgabe des gewaehlten Typs. Der Unterschied ist rechtlich begruendet und wirkt direkt auf
   * die Reichweite: eine Produktankuendigung erreicht per E-Mail nur, wer sie ausdruecklich aktiviert
   * hat. Das muss bei der Typwahl sichtbar sein.
   */
  readonly emailHintKey = computed(() => emailDefaultHintKey(this.selectedTypeKey()));

  readonly missingRequiredTitle = computed(() => !!this.formErrors()?.['missingRequiredTitle']);
  readonly missingBodyLocales = computed<string[]>(() => (this.formErrors()?.['missingBody'] as string[]) ?? []);
  readonly bodyTooLongLocales = computed<string[]>(() => (this.formErrors()?.['bodyTooLong'] as string[]) ?? []);
  readonly missingTenants = computed(() => !!this.formErrors()?.['missingTenants']);
  readonly missingScheduledAt = computed(() => !!this.formErrors()?.['missingScheduledAt']);
  readonly scheduledInPast = computed(() => !!this.formErrors()?.['scheduledInPast']);
  readonly missingDashboardUntil = computed(() => !!this.formErrors()?.['missingDashboardUntil']);
  readonly dashboardUntilBeforeSchedule = computed(() => !!this.formErrors()?.['dashboardUntilBeforeSchedule']);
  readonly dashboardUntilExceedsTtl = computed(() => !!this.formErrors()?.['dashboardUntilExceedsTtl']);
  readonly uriLinkNotAbsolute = computed(() => !!this.formErrors()?.['uriLinkNotAbsolute']);
  readonly uriLinkTooLong = computed(() => !!this.formErrors()?.['uriLinkTooLong']);

  /** Fuer den Hinweistext unter dem Datumsfeld — null, solange kein Typ mit bekannter TTL gewaehlt ist. */
  readonly dashboardTtlDays = computed(() => ANNOUNCEMENT_DASHBOARD_TTL_DAYS[this.selectedTypeKey() ?? ''] ?? null);

  readonly translating = signal(false);
  readonly translationFailures = signal<TranslationFailure[]>([]);

  /**
   * Zielsprachen der Uebersetzung: jede ausser `de`, DEREN TITEL LEER IST.
   *
   * Nicht-destruktiv per Konstruktion — eine befuellte Sprache ist kein Ziel, also kann kein
   * handgeschriebener Text ueberschrieben werden. Das erneute Uebersetzen einer befuellten Sprache
   * braeuchte eine Rueckfrage und ist ausdruecklich nicht Teil dieser Fassung.
   *
   * Quelle ist fest `de`: die einzige Sprache, die der Server erzwingt — und damit der einzige
   * Wert, der seine Pruefung „Quellsprache darf nicht in den Zielsprachen stehen" nicht ausloesen
   * kann.
   */
  readonly translationTargets = computed(() =>
    ANNOUNCEMENT_LOCALES.filter((locale) =>
      locale !== ANNOUNCEMENT_REQUIRED_LOCALE && !this.filledLocales().includes(locale)));

  /** Ohne deutschen Titel gibt es keine Quelle; ohne offene Zielsprache nichts zu tun. */
  readonly canTranslate = computed(() => !this.missingRequiredTitle()
    && this.translationTargets().length > 0
    && !this.translating()
    && !this.submitting());

  // `translating` gehoert dazu: mitten im Nachfuellen abzusenden verschickte einen halb
  // uebersetzten Entwurf.
  readonly canSubmit = computed(() =>
    this.formValid() && !this.submitting() && !this.testing() && !this.translating());

  /**
   * Der Test-Versand prueft serverseitig `content` und `typeKey`, aber NICHT `targeting` — ein
   * Entwurf laesst sich also proben, bevor die Zielgruppe steht. Deshalb haengt der Knopf bewusst
   * nicht an der Gesamtgueltigkeit des Formulars.
   */
  readonly canSendTest = computed(
    () => !!this.selectedTypeKey()
      && !this.missingRequiredTitle()
      && !this.testing()
      && !this.submitting()
      && !this.translating()
  );

  /**
   * Welche der drei Absichten dieser Screen bedient. Kommt als statisches Routen-Datum, NICHT aus
   * einer Auswertung des Pfads: die Routen tragen den Modus ohnehin, und ein geparster Pfad waere
   * eine zweite Wahrheit.
   */
  readonly mode = signal<AnnouncementFormMode>('CREATE');

  /** Id fuer EDIT (dieselbe Ankuendigung) und CORRECT (die richtigzustellende). */
  readonly sourceId = signal<string | null>(null);
  /** Zuletzt gesehene Version — die Sperre von updateAnnouncement. Nur im EDIT-Modus benutzt. */
  readonly sourceVersion = signal<number | null>(null);
  /** Status des geladenen Datensatzes: entscheidet, ob Zielgruppe und Termin noch offen sind. */
  readonly sourceStatus = signal<string | null>(null);

  readonly pageTitleKey = computed(() => ({
    CREATE: 'core.announcement.form.pageTitle',
    EDIT: 'core.announcement.form.pageTitleEdit',
    CORRECT: 'core.announcement.form.pageTitleCorrect'
  }[this.mode()]));

  readonly submitLabelKey = computed(() => ({
    CREATE: 'core.announcement.form.submitAction',
    EDIT: 'core.announcement.form.updateAction',
    CORRECT: 'core.announcement.form.correctAction'
  }[this.mode()]));

  readonly submittingLabelKey = computed(() => ({
    CREATE: 'core.announcement.form.submittingAction',
    EDIT: 'core.announcement.form.updatingAction',
    CORRECT: 'core.announcement.form.correctingAction'
  }[this.mode()]));

  private readonly successMessageKey = computed(() => ({
    CREATE: 'core.announcement.form.createSuccessMessage',
    EDIT: 'core.announcement.form.updateSuccessMessage',
    CORRECT: 'core.announcement.form.correctSuccessMessage'
  }[this.mode()]));

  private readonly errorMessageKey = computed(() => ({
    CREATE: 'core.announcement.form.createErrorMessage',
    EDIT: 'core.announcement.form.updateErrorMessage',
    CORRECT: 'core.announcement.form.correctErrorMessage'
  }[this.mode()]));

  /**
   * Nur solange SCHEDULED sind Zielgruppe und Termin ueberhaupt aenderbar; danach lehnt der Server
   * sie ab. Der Status kommt aus dem geladenen Datensatz, nicht aus einer eigenen Annahme.
   */
  readonly beforeFanout = computed(() => this.sourceStatus() === fromGraphQl.AnnouncementStatus.SCHEDULED);

  /**
   * Der Datensatz aus dem Router-State: beim Duplizieren die Vorlage, bei EDIT/CORRECT der zu
   * bearbeitende Eintrag. Die Liste hat ihn bereits — ein erneuter Query-Aufruf waere reine
   * Latenz, und es gibt ohnehin keine Einzelabfrage im Schema (siehe ngOnInit).
   *
   * Muss im Konstruktor gelesen werden: getCurrentNavigation() liefert nach Abschluss der
   * Navigation null.
   */
  private readonly source: fromGraphQl.Announcement | null;

  constructor() {
    this.mode.set((this.route.snapshot.data['mode'] as AnnouncementFormMode) ?? 'CREATE');

    const state = this.router.getCurrentNavigation()?.extras?.state as { announcement?: fromGraphQl.Announcement };
    this.source = state?.announcement ?? null;

    this.form = this.formBuilder.group(
      {
        typeKey: [null as string | null, Validators.required],
        content: this.formBuilder.group(this.buildContentControls()),
        targetingMode: ['ALL', Validators.required],
        tenantIds: [[] as string[]],
        roles: [[] as string[]],
        scheduleMode: ['LEAD', Validators.required],
        scheduledAt: [null as Date | null],
        dashboardBannerEnabled: [false],
        dashboardUntil: [null as Date | null],
        sticky: [false],
        uriLink: [''],
        // Nur im EDIT-Modus sichtbar und immer optional — er landet in der Revisionshistorie,
        // der Server verlangt ihn nicht.
        reason: ['']
      },
      {
        validators: [
          contentValidator,
          targetingValidator,
          scheduleValidator,
          dashboardUntilValidator,
          uriLinkValidator
        ]
      }
    );

    // statusChanges feuert bei jedem Validierungslauf, also nach jeder Wertaenderung — egal ob sie
    // aus einem Kind-Control oder aus applySource() stammt.
    this.form.statusChanges
      .pipe(startWith(this.form.status), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncFormState());

    // Client-Dok §8.3: mit Dashboard-Fenster sollte die Oberflaeche ein KURZES vorbelegen, sobald
    // sticky angehakt wird — ein unaufhebbares Banner, das eine Woche steht, wird zur Tapete. Auf
    // beide Ausloeser gehorcht (sticky UND dashboardBannerEnabled): egal, welcher der beiden
    // Haken zuletzt gesetzt wird, das Ergebnis muss dasselbe sein.
    this.form.get('sticky')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.prefillShortDashboardWindow());
    this.form.get('dashboardBannerEnabled')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.prefillShortDashboardWindow());
  }

  ngOnInit(): void {
    // EDIT und CORRECT leben vom Router-State der Liste. Das Schema hat KEINE Einzelabfrage —
    // `announcements(status, cursor, limit)` ist die einzige Query —, ein direkter Aufruf der
    // Route oder ein Neuladen hat also nichts zum Nachladen. Seitenweise nach einer Id zu suchen
    // waere fuer diesen Fall unverhaeltnismaessig; zurueck zur Liste ist die ehrliche Antwort.
    //
    // Vor den Ladeaufrufen, nicht danach: sonst laufen drei Queries fuer einen Screen, der im
    // selben Moment verlassen wird.
    if (this.mode() !== 'CREATE' && !this.source) {
      this.toastService.addErrorToast('core.announcement.form.reopenFromListMessage');
      this.router.navigate([ANNOUNCEMENT_LIST_PATH]);
      return;
    }

    this.loadTypeOptions();
    this.loadTenants();
    this.loadRoles();
    this.previewLocale.set(this.currentLocale());
    if (this.source) {
      this.applySource(this.source);
    }
  }

  get contentGroup(): FormGroup {
    return this.form.get('content') as FormGroup;
  }

  localeGroup(locale: string): FormGroup {
    return this.contentGroup.get(locale) as FormGroup;
  }

  /** Fuer die Tab-Koepfe: welche Sprachen sind befuellt? */
  isFilled(locale: string): boolean {
    return this.filledLocales().includes(locale);
  }

  onPreviewLocaleChange(locale: string): void {
    this.previewLocale.set(locale || ANNOUNCEMENT_REQUIRED_LOCALE);
  }

  /**
   * Aufbau der content-Map: `{ locale: { title, body } }`.
   *
   * Nur Sprachen mit nicht-leerem Titel wandern hinein, der Titel getrimmt. `body` ist ein
   * ProseMirror-Baum und wird unveraendert durchgereicht — anders als beim Titel gibt es hier
   * nichts zu trimmen. Eine angefangene und wieder geleerte Sprache hinterlaesst keinen leeren
   * Eintrag — der wuerde beim Rendern den de-Rueckfall aushebeln und einen leeren Titel zustellen.
   */
  buildContent(): fromGraphQl.AnnouncementContent {
    const content: fromGraphQl.AnnouncementContent = {};
    for (const locale of ANNOUNCEMENT_LOCALES) {
      const group = this.localeGroup(locale);
      const title = (group?.get('title')?.value ?? '').trim();
      if (!title) {
        continue;
      }
      content[locale] = { title, body: group?.get('body')?.value };
    }
    return content;
  }

  /**
   * Aufbau der targeting-Map.
   *
   * Zwei UNABHAENGIGE Dimensionen, keine Hierarchie:
   *
   * 1. Mandanten — genau EINER von drei Modi. Der Server zaehlt
   *    `allTenants` + nicht-leere `tenantIds` + `correctsAnnouncement` und verlangt Summe 1
   *    (AnnouncementAdminServiceImpl:840). `correctsAnnouncement` gehoert allein
   *    `correctAnnouncement` und entsteht hier nie. Gesendet werden Mandanten-KURZCODES
   *    (Tenant.tenantId), nicht die UUIDs.
   * 2. Rollen — quer dazu, in dieser Modus-Pruefung ueberhaupt nicht vorhanden. Der Filter wirkt
   *    erst im Fan-out und dort INNERHALB der gewaehlten Mandanten (ordinaryRecipients). `roles`
   *    gehoert deshalb an BEIDE Modi, nicht nur an die Auswahl — `{ allTenants: true,
   *    roles: [...] }` ist ein ausdrueckliches Beispiel des Server-Doks.
   *
   * `roles` faellt weg, wenn nichts gewaehlt ist: eine leere Liste bedeutete dasselbe, aber der
   * fehlende Schluessel ist eindeutig.
   */
  buildTargeting(): fromGraphQl.AnnouncementTargeting {
    const targeting: fromGraphQl.AnnouncementTargeting =
      this.form.get('targetingMode')?.value === 'ALL'
        ? { allTenants: true }
        : { tenantIds: [...((this.form.get('tenantIds')?.value as string[]) ?? [])] };
    const roles = (this.form.get('roles')?.value as string[]) ?? [];
    if (roles.length > 0) {
      targeting.roles = [...roles];
    }
    return targeting;
  }

  /**
   * scheduledAt wird bei „mit Vorlauf senden" GAR NICHT gesetzt — nur dann greift der Server-Vorlauf
   * von 5 Minuten, und nur der ist die Reissleine, in der sich die Ankuendigung noch abbrechen
   * laesst. Die aktuelle Uhrzeit einzutragen waere etwas anderes: der Server prueft den Zeitpunkt
   * nicht gegen die Gegenwart und verschickt beim naechsten Fan-out-Lauf sofort.
   *
   * Mit Angabe: ISO-8601-Instant mit `Z` ueber toISOString() — genau, was Instant.parse erwartet.
   */
  buildInput(): fromGraphQl.CreateAnnouncementInput {
    const input: fromGraphQl.CreateAnnouncementInput = {
      typeKey: this.form.get('typeKey')?.value,
      content: this.buildContent(),
      targeting: this.buildTargeting(),
      sticky: this.form.get('sticky')?.value === true
    };
    const scheduledAt = this.scheduledAtIso();
    if (scheduledAt) {
      input.scheduledAt = scheduledAt;
    }
    const dashboardUntil = this.dashboardUntilIso();
    if (dashboardUntil) {
      input.dashboardUntil = dashboardUntil;
    }
    const uriLink = this.uriLinkValue();
    if (uriLink) {
      input.uriLink = uriLink;
    }
    return input;
  }

  /**
   * buildInput() plus Idempotenzschluessel — fuer createAnnouncement und correctAnnouncement,
   * NICHT fuer den Testversand und NICHT fuer updateAnnouncement (dort ist `version` die Sperre).
   */
  buildCreateInput(): fromGraphQl.CreateAnnouncementInput {
    return { ...this.buildInput(), clientRequestId: this.clientRequestId };
  }

  /**
   * Eingabe fuer updateAnnouncement.
   *
   * `content` geht immer mit — der Server ersetzt es und schreibt das alte in die
   * Revisionshistorie. Zielgruppe und Termin nur, solange noch nichts verschickt wurde: danach
   * lehnt der Server sie ab („targeting and scheduledAt are frozen once fan-out started"), und
   * ein WEGGELASSENES Feld ist dort etwas anderes als ein null-Feld.
   */
  buildUpdateInput(): fromGraphQl.UpdateAnnouncementInput {
    const input: fromGraphQl.UpdateAnnouncementInput = {
      version: this.sourceVersion() ?? 0,
      content: this.buildContent(),
      // Anders als targeting/scheduledAt unten NICHT an beforeFanout() gebunden (Client-Dok §8.3)
      // und deshalb IMMER explizit gesendet: ein erneut gesendeter Zeitstempel wirkt wie
      // "unveraendert", ein leerer String nimmt das Banner bewusst ab.
      dashboardUntil: this.dashboardUntilIso() ?? '',
      // Dieselbe Dreiwertigkeit wie dashboardUntil (Client-Dok §8.7), aus demselben Grund immer
      // explizit gesendet.
      uriLink: this.uriLinkValue() ?? '',
      // Kein Fan-out-Vorbehalt und keine Dreiwertigkeit noetig — ein normales Boolean, immer
      // explizit mitgeschickt (Client-Dok §8.3).
      sticky: this.form.get('sticky')?.value === true
    };
    if (this.beforeFanout()) {
      input.targeting = this.buildTargeting();
      const scheduledAt = this.scheduledAtIso();
      if (scheduledAt) {
        input.scheduledAt = scheduledAt;
      }
    }
    const reason = ((this.form.get('reason')?.value as string) ?? '').trim();
    if (reason) {
      input.reason = reason;
    }
    return input;
  }

  /**
   * Der Termin als ISO-8601-Instant mit `Z` — genau, was Instant.parse erwartet — oder null bei
   * „mit Vorlauf senden".
   *
   * Bei Vorlauf wird `scheduledAt` GAR NICHT gesetzt: nur dann greift der Server-Vorlauf von fuenf
   * Minuten, und nur der ist die Reissleine, in der sich die Ankuendigung noch abbrechen laesst.
   * Die aktuelle Uhrzeit einzutragen waere etwas anderes — der Server prueft den Zeitpunkt nicht
   * gegen die Gegenwart und verschickt beim naechsten Fan-out-Lauf sofort.
   *
   * Eine Stelle fuer beide Eingaben (create und update): zwei Umwandlungen liefen leicht
   * auseinander.
   */
  private scheduledAtIso(): string | null {
    if (this.form.get('scheduleMode')?.value !== 'AT') {
      return null;
    }
    const scheduledAt = this.form.get('scheduledAt')?.value as Date | null;
    return scheduledAt ? new Date(scheduledAt).toISOString() : null;
  }

  /** null, solange der Schalter aus ist oder kein Datum steht — dann gibt es kein Dashboard-Banner. */
  private dashboardUntilIso(): string | null {
    if (!this.form.get('dashboardBannerEnabled')?.value) {
      return null;
    }
    const dashboardUntil = this.form.get('dashboardUntil')?.value as Date | null;
    return dashboardUntil ? new Date(dashboardUntil).toISOString() : null;
  }

  /** null, solange das Feld leer ist — dann gibt es kein externes Ziel. */
  private uriLinkValue(): string | null {
    const uriLink = ((this.form.get('uriLink')?.value as string) ?? '').trim();
    return uriLink || null;
  }

  /**
   * Kurzes Standardfenster (zwei Tage), sobald sticky UND das Dashboard-Banner aktiv sind, aber noch
   * kein Termin steht (Client-Dok §8.3). Rein ein Vorschlag — der Wert bleibt im Datumsfeld aenderbar
   * und wird NICHT ueberschrieben, sobald schon einer steht: sonst risse ein erneutes An-/Abhaken
   * einen bereits gewaehlten Termin wieder heraus.
   */
  private prefillShortDashboardWindow(): void {
    const sticky = this.form.get('sticky')?.value === true;
    const dashboardBannerEnabled = this.form.get('dashboardBannerEnabled')?.value === true;
    const dashboardUntil = this.form.get('dashboardUntil')?.value as Date | null;
    if (sticky && dashboardBannerEnabled && !dashboardUntil) {
      this.form.patchValue({ dashboardUntil: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000) });
    }
  }

  onSubmit(): void {
    if (!this.canSubmit()) {
      this.form.markAllAsTouched();
      return;
    }
    this.submitting.set(true);
    this.serverErrors.set([]);

    this.submitObservable()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.addToastWithMessage(this.translate.instant(this.successMessageKey()));
          this.submitting.set(false);
          this.router.navigate([ANNOUNCEMENT_LIST_PATH]);
        },
        error: (error) => this.handleSubmitError(error)
      });
  }

  /**
   * Ein Modus, eine Mutation. Die Variablen unterscheiden sich, der Ablauf drumherum nicht —
   * deshalb hier nur der Aufruf und kein zweiter subscribe-Block.
   *
   * mutateObservable mit { next, error }: mutate() und mutateAndExecute() subscriben ohne
   * error-Callback und verschlucken den Fehler (apollo.service.ts:11-35).
   */
  private submitObservable(): Observable<unknown> {
    const id = this.sourceId();
    switch (this.mode()) {
      case 'EDIT':
        return this.apolloService.mutateObservable<fromGraphQl.UpdateAnnouncementResponse>(
          fromGraphQl.UPDATE_ANNOUNCEMENT_MUTATION, { id, input: this.buildUpdateInput() });
      case 'CORRECT':
        return this.apolloService.mutateObservable<fromGraphQl.CorrectAnnouncementResponse>(
          fromGraphQl.CORRECT_ANNOUNCEMENT_MUTATION, { id, input: this.buildCreateInput() });
      default:
        return this.apolloService.mutateObservable<fromGraphQl.CreateAnnouncementResponse>(
          fromGraphQl.CREATE_ANNOUNCEMENT_MUTATION, { input: this.buildCreateInput() });
    }
  }

  /**
   * Drei Fehlerlagen, drei Antworten. Zwei davon sind KEINE Bedienfehler und duerfen deshalb nicht
   * als „hat nicht funktioniert" enden:
   *
   * - Versionskonflikt: jemand anderes war schneller. Damit ist der eigene Listenstand veraltet —
   *   Meldung UND zurueck zur Liste, damit der Admin auf dem aktuellen Text weiterarbeitet.
   *   Erkennungsmerkmal ist „changed by someone else", nicht das Wort „version": der Server wirft
   *   das an zwei Stellen (Handpruefung beim Laden, Hibernate-Sperre beim Flush) und formuliert
   *   beide ausdruecklich gleich, weil das Gegenmittel dasselbe ist.
   * - Bereits angelegt: das Anlegen ist durchgelaufen, die Antwort ging verloren, danach wurde der
   *   Text geaendert. Der Servertext liest sich wie ein Fehler, beschreibt aber einen Erfolg.
   * - Alles andere: fachliche Ablehnung. Sie steht wortwoertlich am Inhalts-Abschnitt (§8.1) und
   *   das Formular bleibt stehen, damit der Autor seinen Entwurf behaelt.
   *
   * Beide Erkennungen laufen absichtlich grob ueber einen Teilstring. Die wortwoertliche Meldung
   * ist ohnehin sichtbar, ein Fehlgriff verschluckt also keine Information.
   */
  private handleSubmitError(error: unknown): void {
    const messages = serverErrorMessages(error);
    this.serverErrors.set(messages);
    this.submitting.set(false);
    const joined = messages.join(' ');

    if (joined.includes('changed by someone else')) {
      this.toastService.addErrorToast('core.announcement.form.versionConflictMessage');
      this.router.navigate([ANNOUNCEMENT_LIST_PATH]);
      return;
    }
    if (joined.includes('clientRequestId')) {
      this.toastService.addErrorToast('core.announcement.form.alreadyCreatedMessage');
      this.router.navigate([ANNOUNCEMENT_LIST_PATH]);
      return;
    }
    this.toastService.addErrorToast(this.errorMessageKey());
  }

  /**
   * Test-Versand: schickt den Entwurf NUR an den Aufrufer und legt nichts an — es entsteht kein
   * Datensatz in der Liste. Es laeuft aber durch die normale Pipeline, die eigene E-Mail bzw. Push
   * geht also wirklich raus.
   *
   * `targeting` steht im Input, weil der Typ es verlangt; ausgewertet wird es serverseitig nicht.
   */
  onSendTest(): void {
    if (!this.canSendTest()) {
      return;
    }
    this.testing.set(true);
    this.serverErrors.set([]);

    this.apolloService
      .mutateObservable<fromGraphQl.SendTestAnnouncementResponse>(fromGraphQl.SEND_TEST_ANNOUNCEMENT_MUTATION, {
        input: this.buildInput(),
        locale: this.previewLocale()
      })
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.addToastWithMessage(this.translate.instant('core.announcement.form.testSuccessMessage'));
          this.testing.set(false);
        },
        error: (error) => {
          this.serverErrors.set(serverErrorMessages(error));
          this.toastService.addErrorToast('core.announcement.form.testErrorMessage');
          this.testing.set(false);
        }
      });
  }

  /**
   * Fehlende Sprachfassungen maschinell vorbefuellen (Client-Dok §8.5).
   *
   * Das Ergebnis ist ein ENTWURF: es landet im Editor und durchlaeuft beim Speichern dieselbe
   * Allow-List-Pruefung wie handgeschriebener Text. Der Endpunkt ersetzt keine Validierung.
   */
  onTranslate(): void {
    if (!this.canTranslate()) {
      return;
    }
    this.translating.set(true);
    this.translationFailures.set([]);
    this.serverErrors.set([]);

    const targets = [...this.translationTargets()];
    const sourceGroup = this.localeGroup(ANNOUNCEMENT_REQUIRED_LOCALE);

    this.apolloService
      .mutateObservable<TranslateTextsResponse>(TRANSLATE_TEXTS_MUTATION, {
        input: {
          sourceLocale: ANNOUNCEMENT_REQUIRED_LOCALE,
          targetLocales: targets,
          segments: buildTranslationSegments({
            title: ((sourceGroup?.get('title')?.value as string) ?? '').trim(),
            body: sourceGroup?.get('body')?.value as JSONContent | null
          })
        }
      })
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          const translation = result?.data?.translateTexts;
          for (const locale of translation?.translations ?? []) {
            // emitEvent: false wie in applySource — sonst meldet jedes Patchen eine
            // Nutzeraenderung zurueck und markiert das Formular fuenfmal als dirty.
            this.localeGroup(locale.locale)?.patchValue(
              contentEntryFromSegments(locale.segments), { emitEvent: false });
          }
          // Von Hand, weil emitEvent: false die statusChanges-Kette nicht anstoesst: sonst
          // bleiben die Punkte an den Tab-Koepfen aus und die Validatoren sehen den neuen Stand
          // nicht.
          this.form.updateValueAndValidity();
          this.syncFormState();
          // IMMER setzen, auch wenn Sprachen durchgekommen sind: Teilerfolg ist der Normalfall,
          // und ein stilles „fertig" nach vier von sechs Sprachen verliert genau die Information,
          // fuer die es diese Liste gibt (Client-Dok §8.5).
          this.translationFailures.set(translation?.failures ?? []);
          this.translating.set(false);
        },
        error: (error) => {
          // Etwas anderes als `failures`: die Anfrage SELBST wurde abgelehnt (unbekannte Sprache,
          // zu viele Zeichen, fehlende Rolle) — dann gibt es keine Teilergebnisse, sondern eine
          // Sammelmeldung.
          this.serverErrors.set(serverErrorMessages(error));
          this.toastService.addErrorToast('core.announcement.form.translateErrorMessage');
          this.translating.set(false);
        }
      });
  }

  /** Fuer das Template: eigener Text je Grund, sonst die woertliche Servermeldung. */
  failureKey(failure: TranslationFailure): string | null {
    return translationFailureKey(failure?.reason);
  }

  onAbort(): void {
    this.router.navigate([ANNOUNCEMENT_LIST_PATH]);
  }

  // --- interne Helfer

  private syncFormState(): void {
    this.formErrors.set(this.form.errors);
    this.formValid.set(this.form.valid);
    this.targetingMode.set(this.form.get('targetingMode')?.value ?? 'ALL');
    this.scheduleMode.set(this.form.get('scheduleMode')?.value ?? 'LEAD');
    this.dashboardBannerEnabled.set(this.form.get('dashboardBannerEnabled')?.value ?? false);
    this.selectedTypeKey.set(this.form.get('typeKey')?.value ?? null);
    this.filledLocales.set(
      ANNOUNCEMENT_LOCALES.filter((locale) => !!(this.localeGroup(locale)?.get('title')?.value ?? '').trim())
    );
  }

  private currentLocale(): string {
    return this.translate.currentLang ?? this.translate.getDefaultLang() ?? ANNOUNCEMENT_REQUIRED_LOCALE;
  }

  private buildContentControls(): Record<string, FormGroup> {
    const controls: Record<string, FormGroup> = {};
    for (const locale of ANNOUNCEMENT_LOCALES) {
      controls[locale] = this.formBuilder.group({ title: [''], body: [null as JSONContent | null] });
    }
    return controls;
  }

  /**
   * Vorbelegen aus einem bestehenden Eintrag — beim Duplizieren, Bearbeiten und Korrigieren
   * derselbe Weg. Rein clientseitig, kein API-Aufruf.
   *
   * Der TERMIN ist der einzige Unterschied zwischen den Faellen, und er ist begruendet:
   *
   * - Duplizieren und Korrigieren nehmen ihn NICHT. Die Vorlage liegt praktisch immer in der
   *   Vergangenheit, und der Server prueft den Zeitpunkt nicht gegen die Gegenwart — ein
   *   uebernommener Vergangenheitstermin verschickt beim naechsten Fan-out-Lauf sofort an alle.
   * - Bearbeiten nimmt ihn, SOFERN er in der Zukunft liegt. Sonst verliert ein Admin, der nur
   *   einen Tippfehler ausbessert, den gesetzten Termin seiner noch nicht versendeten
   *   Ankuendigung.
   *
   * Der Zukunftstest steht hier und nicht am Status: eine SCHEDULED-Ankuendigung, deren Termin
   * waehrend des Bearbeitens verstreicht, faellt damit von selbst auf den Vorlauf zurueck.
   */
  private applySource(source: fromGraphQl.Announcement): void {
    for (const locale of ANNOUNCEMENT_LOCALES) {
      const entry = source.content?.[locale];
      this.localeGroup(locale)?.patchValue(
        {
          title: entry?.title ?? '',
          // Kopie, nicht Referenz: der Router-State haelt dieselben Objekte wie die Liste, und
          // ein geteilter Baum zwischen Editor und Listenzeile faellt erst Wochen spaeter auf.
          body: entry?.body ? structuredClone(entry.body) : null
        },
        { emitEvent: false }
      );
    }

    // targeting ist ein pass-through-Map-Scalar und kann alles enthalten — defensiv lesen.
    const allTenants = source.targeting?.allTenants === true;
    const tenantIds = Array.isArray(source.targeting?.tenantIds) ? source.targeting.tenantIds : [];
    const roles = Array.isArray(source.targeting?.roles) ? source.targeting.roles : [];

    const keepSchedule = this.mode() === 'EDIT'
      && !!source.scheduledAt
      && new Date(source.scheduledAt).getTime() > Date.now();

    // Anders als scheduledAt OHNE Zukunftstest uebernommen: dashboardUntil bleibt auch nach dem
    // Fan-out aenderbar (Client-Dok §8.3), ein bereits abgelaufenes Banner darf hier trotzdem
    // sichtbar bleiben, damit der Admin es bewusst verlaengert oder abnimmt statt es blind neu
    // zu setzen. Beim Duplizieren (CREATE aus einer Vorlage) und bei CORRECT NICHT uebernommen —
    // dieselbe Begruendung wie beim Termin: die Vorlage ist eine neue Absendeabsicht.
    const keepDashboardUntil = this.mode() === 'EDIT' && !!source.dashboardUntil;

    // uriLink aus demselben Grund wie dashboardUntil UNGEACHTET des Fan-out-Status uebernommen
    // (Client-Dok §8.7 — auch danach aenderbar), aber wie beim Termin NICHT beim Duplizieren/
    // Korrigieren: die Vorlage ist eine neue Absendeabsicht, kein Fortfuehren der alten.
    const keepUriLink = this.mode() === 'EDIT' && !!source.uriLink;

    this.form.patchValue({
      typeKey: source.typeKey ?? null,
      targetingMode: allTenants ? 'ALL' : 'SELECTION',
      tenantIds: [...tenantIds],
      roles: [...roles],
      scheduleMode: keepSchedule ? 'AT' : 'LEAD',
      scheduledAt: keepSchedule ? new Date(source.scheduledAt) : null,
      dashboardBannerEnabled: keepDashboardUntil,
      dashboardUntil: keepDashboardUntil ? new Date(source.dashboardUntil as string) : null,
      // Anders als Termin/Dashboard-Fenster/Ziel IMMER uebernommen, auch beim Duplizieren und
      // Korrigieren: sticky ist eine Eigenschaft der Nachricht wie ihr Inhalt, keine neue
      // Absendeabsicht wie ein Zeitpunkt.
      sticky: source.sticky ?? false,
      uriLink: keepUriLink ? source.uriLink : ''
    });

    this.sourceId.set(source.id ?? null);
    this.sourceVersion.set(source.version ?? null);
    this.sourceStatus.set(source.status ?? null);
  }

  /**
   * Typ-Auswahl aus dem Katalog (#735-Query). Der Filter auf die Announcement-Kategorie ist tolerant
   * und faellt auf den vollen Katalog zurueck — welchen Wert der Server in categoryKey schickt, ist
   * frontendseitig nicht belegbar, und eine leere Auswahl machte das Formular unbenutzbar.
   */
  private loadTypeOptions(): void {
    this.apolloService
      .query<fromCatalog.CatalogQueryResponse>(fromCatalog.NOTIFICATION_CATALOG_QUERY, { locale: this.currentLocale() })
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => this.typeOptions.set(announcementTypeOptions(result?.data?.catalog ?? [])),
        error: () => this.typeOptions.set([])
      });
  }

  /**
   * Mandantenliste fuer die Zielgruppe. Gesendet wird spaeter `tenantId` (Kurzcode), nicht `id`
   * (UUID) — ein Tippfehler waere serverseitig eine ValidationProblem, deshalb ueberhaupt eine
   * Auswahl statt eines Textfelds.
   *
   * Faellt die Query aus, bleibt die Auswahl leer und das Feld traegt einen Hinweis; „Alle Mandanten"
   * funktioniert weiter. Die Query haengt im Web bisher an framework_tenants_manage, nicht an
   * framework_nexato_employee.
   */
  private loadTenants(): void {
    this.apolloService
      .query<TenantsQueryResponse>(TENANTS_QUERY, {
        pageNumber: 0,
        pageSize: TENANT_PAGE_SIZE,
        sortProperty: 'name',
        sortDirection: 'ASC'
      })
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          const content = result?.data?.tenants?.content ?? [];
          this.tenants.set(content.map((tenant: any) => new Tenant(tenant)));
          this.tenantsFailed.set(false);
        },
        error: () => {
          this.tenants.set([]);
          this.tenantsFailed.set(true);
        }
      });
  }

  /**
   * Rollenliste. `roles` wird serverseitig NICHT validiert — ein Tippfehler erreicht still niemand.
   * Genau deshalb eine Auswahl aus getRoles statt einer Freitexteingabe.
   */
  private loadRoles(): void {
    this.rolesService
      .getRoles()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (roles) => this.roles.set(roles ?? []),
        error: () => this.roles.set([])
      });
  }
}

/**
 * `de` braucht einen nicht-leeren Titel — die einzige inhaltliche Serverpruefung, die den Titel
 * betrifft.
 *
 * `body` ist hier ein Pflichtfeld, sobald in einer Sprache ein Titel steht: eine Ankuendigung ohne
 * Text ist kein Anwendungsfall, sondern ein vergessenes Feld (Entscheidung 7 der Spec) — das ist
 * strenger als der Server, der einen fehlenden Body zulaesst. Eine ganz leere Sprache bleibt leer
 * und wird gar nicht erst in content aufgenommen.
 *
 * Der Server prueft `body`, der vorhanden ist, sehr wohl — gegen die Allow-List aus
 * announcement-rich-text.ts (Client-Dok §8.1). Die Zeichengrenze ist Teil dieser Pruefung und wird
 * deshalb hier ebenfalls gesperrt, nicht nur der leere Fall.
 */
export const contentValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const content = control.get('content');
  if (!content) {
    return null;
  }
  const errors: ValidationErrors = {};
  const missingBody: string[] = [];
  const bodyTooLong: string[] = [];

  for (const locale of ANNOUNCEMENT_LOCALES) {
    const group = content.get(locale);
    const title = (group?.get('title')?.value ?? '').trim();
    const body = group?.get('body')?.value as JSONContent | null;
    if (!title) {
      continue;
    }
    if (isEmptyRichText(body)) {
      missingBody.push(locale);
    }
    // Der Server lehnt darueber ab (Client-Dok §8.1). Hier sperren statt nur warnen: die
    // Ablehnung kaeme sonst erst nach dem Absenden.
    if (richTextLength(body) > ANNOUNCEMENT_BODY_MAX_CHARS) {
      bodyTooLong.push(locale);
    }
  }

  const requiredTitle = (content.get(ANNOUNCEMENT_REQUIRED_LOCALE)?.get('title')?.value ?? '').trim();
  if (!requiredTitle) {
    errors['missingRequiredTitle'] = true;
  }
  if (missingBody.length > 0) {
    errors['missingBody'] = missingBody;
  }
  if (bodyTooLong.length > 0) {
    errors['bodyTooLong'] = bodyTooLong;
  }
  return Object.keys(errors).length > 0 ? errors : null;
};

/**
 * Genau eines von beidem: `allTenants: true` ODER eine nicht-leere `tenantIds`-Liste. Der Server
 * prueft `allTenants() == hasTenantIds()`; „Auswahl" ohne einen einzigen Mandanten waere also keines
 * von beidem und damit eine ValidationProblem.
 */
export const targetingValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (control.get('targetingMode')?.value !== 'SELECTION') {
    return null;
  }
  const tenantIds = (control.get('tenantIds')?.value as string[]) ?? [];
  return tenantIds.length === 0 ? { missingTenants: true } : null;
};

/**
 * Der Server prueft den Zeitpunkt NICHT gegen die Gegenwart: ein Termin in der Vergangenheit wird
 * akzeptiert und beim naechsten Lauf des minuetlichen Fan-out-Jobs sofort versendet. Ein vertippter
 * Jahreswechsel verschickt also augenblicklich an alle — deshalb faengt das Formular ihn ab, bevor
 * die Mutation laeuft.
 */
export const scheduleValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (control.get('scheduleMode')?.value !== 'AT') {
    return null;
  }
  const scheduledAt = control.get('scheduledAt')?.value;
  if (!scheduledAt) {
    return { missingScheduledAt: true };
  }
  return new Date(scheduledAt).getTime() <= Date.now() ? { scheduledInPast: true } : null;
};

/**
 * Client-Dok §8.3: `dashboardUntil` muss NACH `scheduledAt` liegen und darf die Lebensdauer der
 * Nachricht nicht ueberschreiten (7 Tage `announcement.maintenance`, 30 Tage `announcement.product`).
 *
 * Anker fuer "nach scheduledAt": bei Vorlauf (LEAD) kennt der Client den exakten Sendezeitpunkt
 * nicht (Server-Vorlauf +5 Min, siehe scheduledAtIso()) — "jetzt" ist die naechstbeste Annaeherung.
 * Dieselbe Annaeherung greift, wenn der Termin nach dem Fan-out bereits eingefroren und in der
 * Vergangenheit liegt (das Feld wird dann gar nicht mehr angezeigt): ein Banner, dessen Ende schon
 * in der Vergangenheit liegt, ist ohnehin fehlerhaft, "jetzt" ist hier also die staerkere, sinnvolle
 * Regel und keine Verwaesserung.
 */
export const dashboardUntilValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (!control.get('dashboardBannerEnabled')?.value) {
    return null;
  }
  const dashboardUntil = control.get('dashboardUntil')?.value;
  if (!dashboardUntil) {
    return { missingDashboardUntil: true };
  }
  const dashboardUntilMs = new Date(dashboardUntil).getTime();

  const scheduleMode = control.get('scheduleMode')?.value;
  const scheduledAt = control.get('scheduledAt')?.value;
  const anchorMs = scheduleMode === 'AT' && scheduledAt ? new Date(scheduledAt).getTime() : Date.now();

  if (dashboardUntilMs <= anchorMs) {
    return { dashboardUntilBeforeSchedule: true };
  }

  const ttlDays = ANNOUNCEMENT_DASHBOARD_TTL_DAYS[control.get('typeKey')?.value as string];
  if (ttlDays && dashboardUntilMs > anchorMs + ttlDays * 24 * 60 * 60 * 1000) {
    return { dashboardUntilExceedsTtl: true };
  }

  return null;
};

/**
 * Client-Dok §8.7: `uriLink` ist optional, aber wenn gesetzt eine absolute `http`/`https`-URL,
 * hoechstens 2048 Zeichen — insbesondere kein `mailto:` (anders als der Link im Rich-Text-Body,
 * siehe announcement-rich-text.ts) und keine relative Adresse.
 */
export const uriLinkValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const uriLink = ((control.get('uriLink')?.value as string) ?? '').trim();
  if (!uriLink) {
    return null;
  }
  if (uriLink.length > URI_LINK_MAX_CHARS) {
    return { uriLinkTooLong: true };
  }
  return /^https?:\/\/.+/i.test(uriLink) ? null : { uriLinkNotAbsolute: true };
};
